import React from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import {
  AlertTriangle,
  Clock,
  DollarSign,
  Calendar,
  FileText,
  ExternalLink,
  X,
  ShieldCheck,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { cn, formatCurrency } from "@/lib/utils";

export interface LoanConflictData {
  loanCode: string;
  amount: number;
  status: string;
  requestDate: string;
  purpose?: string;
}

interface LoanConflictModalProps {
  isOpen: boolean;
  onClose: () => void;
  conflictCode: "ACTIVE_LOAN_EXISTS" | "DUPLICATE_APPLICATION";
  conflictData: LoanConflictData;
  onSubmitAnyway?: () => void;
  isAdmin?: boolean;
  isPresident?: boolean;
}

const statusColors: Record<string, string> = {
  pending: "bg-amber-100 text-amber-700 border-amber-200",
  approved: "bg-green-100 text-green-700 border-green-200",
  disbursed: "bg-blue-100 text-blue-700 border-blue-200",
  active: "bg-blue-100 text-blue-700 border-blue-200",
  rejected: "bg-red-100 text-red-700 border-red-200",
  completed: "bg-emerald-100 text-emerald-700 border-emerald-200",
};

const statusLabels: Record<string, string> = {
  pending: "Pending Review",
  approved: "Approved",
  disbursed: "Disbursed",
  active: "Active",
  rejected: "Rejected",
  completed: "Completed",
};

export default function LoanConflictModal({
  isOpen,
  onClose,
  conflictCode,
  conflictData,
  onSubmitAnyway,
  isAdmin = false,
  isPresident = false,
}: LoanConflictModalProps) {
  const navigate = useNavigate();
  const isDuplicate = conflictCode === "DUPLICATE_APPLICATION";
  const canSubmitAnyway = isAdmin || isPresident;

  const handleViewExisting = () => {
    onClose();
    navigate("/loans");
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md p-0 gap-0 overflow-hidden">
        {/* Header with warning icon */}
        <DialogHeader className="px-6 pt-6 pb-4">
          <div className="flex items-start gap-3">
            <div
              className={cn(
                "h-10 w-10 rounded-full flex items-center justify-center shrink-0",
                isDuplicate ? "bg-amber-100" : "bg-orange-100"
              )}
            >
              <AlertTriangle
                className={cn(
                  "h-5 w-5",
                  isDuplicate ? "text-amber-600" : "text-orange-600"
                )}
              />
            </div>
            <div className="flex-1 min-w-0">
              <DialogTitle className="text-base font-semibold text-gray-900 text-left">
                {isDuplicate
                  ? "Possible Duplicate Application"
                  : "Active Loan Found"}
              </DialogTitle>
              <DialogDescription className="text-sm text-gray-500 mt-1 text-left">
                {isDuplicate
                  ? "This looks like a duplicate submission. Please review the existing application."
                  : "This member already has a pending or active loan."}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <Separator />

        {/* Conflict details card */}
        <div className="px-6 py-4">
          <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3">
              Existing Loan Details
            </p>

            <div className="grid grid-cols-2 gap-3">
              {/* Loan ID */}
              <div className="flex items-center gap-2">
                <FileText className="h-3.5 w-3.5 text-gray-400" />
                <div>
                  <p className="text-[10px] text-gray-400 uppercase">Loan ID</p>
                  <p className="text-sm font-semibold text-gray-800 font-mono">
                    {conflictData.loanCode}
                  </p>
                </div>
              </div>

              {/* Amount */}
              <div className="flex items-center gap-2">
                <DollarSign className="h-3.5 w-3.5 text-gray-400" />
                <div>
                  <p className="text-[10px] text-gray-400 uppercase">Amount</p>
                  <p className="text-sm font-semibold text-gray-800">
                    {formatCurrency(conflictData.amount)}
                  </p>
                </div>
              </div>

              {/* Status */}
              <div className="flex items-center gap-2">
                <Clock className="h-3.5 w-3.5 text-gray-400" />
                <div>
                  <p className="text-[10px] text-gray-400 uppercase">Status</p>
                  <Badge
                    variant="secondary"
                    className={cn(
                      "text-[10px] px-2 py-0 mt-0.5 border",
                      statusColors[conflictData.status] ||
                        "bg-gray-100 text-gray-700 border-gray-200"
                    )}
                  >
                    {statusLabels[conflictData.status] || conflictData.status}
                  </Badge>
                </div>
              </div>

              {/* Request Date */}
              <div className="flex items-center gap-2">
                <Calendar className="h-3.5 w-3.5 text-gray-400" />
                <div>
                  <p className="text-[10px] text-gray-400 uppercase">
                    Request Date
                  </p>
                  <p className="text-sm font-semibold text-gray-800">
                    {conflictData.requestDate
                      ? format(new Date(conflictData.requestDate), "MMM dd, yyyy")
                      : "N/A"}
                  </p>
                </div>
              </div>
            </div>

            {/* Purpose (if available) */}
            {conflictData.purpose && (
              <div className="mt-3 pt-3 border-t border-gray-200">
                <p className="text-[10px] text-gray-400 uppercase mb-1">
                  Purpose
                </p>
                <p className="text-sm text-gray-600 line-clamp-2">
                  {conflictData.purpose}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Info note */}
        {canSubmitAnyway && (
          <div className="px-6 pb-2">
            <div className="flex items-start gap-2 bg-blue-50 rounded-lg p-3 border border-blue-100">
              <ShieldCheck className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />
              <p className="text-xs text-blue-700">
                As an administrator, you can override this conflict and submit a
                new loan application if policy allows.
              </p>
            </div>
          </div>
        )}

        {/* Actions */}
        <DialogFooter className="px-6 py-4 bg-gray-50 border-t">
          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 w-full">
            <Button
              variant="outline"
              size="sm"
              onClick={handleViewExisting}
              className="gap-1.5"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              View Existing Loan
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="gap-1.5 text-gray-600"
            >
              <X className="h-3.5 w-3.5" />
              Cancel
            </Button>
            {canSubmitAnyway && onSubmitAnyway && (
              <Button
                size="sm"
                onClick={onSubmitAnyway}
                className="gap-1.5 bg-[#009A44] hover:bg-[#008238] text-white"
              >
                Submit Anyway
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
